/**
 * Message — one chat line in the legacy SWF format.
 *
 * Faithful inline format from the screenshots (m0de, musicdemon, SephyxCore):
 *
 *   [12:34 PM] <name>: <message text>
 *
 * Single horizontal line per message, wrapping with hanging indent. The
 * timestamp is dark-navy, the username uses the user's rank colour (with
 * the colon appended via CSS ::after), the body is dark-navy.
 *
 * System / error / PM / sound-message / chat-cmd variants reuse the same
 * inline pattern but with extra tag elements (e.g. "PM" marker).
 */
import type { ChatMessage } from '../lib/types.js';
import { renderStyledBody } from '../lib/styledText.js';

interface Props { m: ChatMessage; mention?: boolean; }

function hhmm(ts: number): string {
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
}

const KIND_CLS: Record<string, string> = {
    say:           'msg-say',
    pm:            'msg-pm',
    generic_mess:  'msg-generic',
    user_mess:     'msg-user-mess',
    blank_mess:    'msg-blank',
    sound_message: 'msg-sound',
    chat_cmd:      'msg-cmd',
    system:        'msg-system',
    error:         'msg-error',
    'mod-buzz':    'msg-mod msg-mod-buzz',
    'mod-slap':    'msg-mod msg-mod-slap',
    'mod-ban':     'msg-mod msg-mod-ban',
    'mod-mute':    'msg-mod msg-mod-mute',
    'mod-bomb':    'msg-mod msg-mod-bomb',
    'mod-walrus':  'msg-mod msg-mod-walrus',
    'mod-bucket':  'msg-mod msg-mod-bucket',
};

export function Message({ m, mention }: Props) {
    const cls = `msg ${KIND_CLS[m.kind] ?? 'msg-system'}${mention ? ' msg-mention' : ''}`;
    const t = <span className="msg-time">[{hhmm(m.t)}]</span>;

    if (m.kind === 'system' || m.kind === 'error'
        || m.kind === 'mod-buzz' || m.kind === 'mod-slap'
        || m.kind === 'mod-ban'  || m.kind === 'mod-mute'
        || m.kind === 'mod-bomb' || m.kind === 'mod-walrus' || m.kind === 'mod-bucket') {
        return (
            <div className={cls}>
                {t} <span className="msg-text">{renderStyledBody(m.body)}</span>
            </div>
        );
    }
    if (m.kind === 'pm') {
        return (
            <div className={cls}>
                {t} <span className="msg-pm-marker">PM</span>{' '}
                <span className="msg-name" style={{ color: m.from?.color }}>{m.from?.name}</span>{' '}
                <span className="msg-text">{renderStyledBody(m.body)}</span>
            </div>
        );
    }
    if (m.kind === 'sound_message') {
        return (
            <div className={cls}>
                {t}{' '}
                <span className="msg-name" style={{ color: m.from?.color }}>{m.from?.name}</span>{' '}
                <span className="msg-sound-tag">♪ {m.sound_p}</span>
                {m.body && m.body !== `[${m.sound_p}]` && (
                    <> <span className="msg-text">{renderStyledBody(m.body)}</span></>
                )}
            </div>
        );
    }
    if (m.kind === 'chat_cmd') {
        return (
            <div className={cls}>
                {t}{' '}
                <span className="msg-name" style={{ color: m.from?.color }}>{m.from?.name}</span>{' '}
                <span className="msg-text msg-cmd-text">{m.body}</span>
            </div>
        );
    }
    // say / generic_mess / user_mess / blank_mess / default
    // Special: `/me <action>` say → italic action line, "* name action"
    if (m.kind === 'say' && typeof m.body === 'string' && m.body.startsWith('/me ')) {
        const action = m.body.slice(4);
        return (
            <div className={`${cls} msg-action`}>
                {t}{' '}
                <span className="msg-action-star">*</span>{' '}
                <span className="msg-name" style={{ color: m.from?.color }}>{m.from?.name}</span>{' '}
                <span className="msg-text">{renderStyledBody(action)}</span>
            </div>
        );
    }
    return (
        <div className={cls}>
            {t}{' '}
            <span className="msg-name" style={{ color: m.from?.color }}>{m.from?.name}</span>{' '}
            <span className="msg-text">{renderStyledBody(m.body)}</span>
        </div>
    );
}
